import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowRight, 
  ArrowLeft,
  Loader2, 
  Video, 
  Sparkles, 
  CheckCircle2, 
  Copy, 
  RefreshCw, 
  Save,
  FolderOpen,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { renderStep6, renderScenePromptDetail } from '../components/MonicaStep6New';
import { renderStepFinal } from '../components/MonicaStepFinal';

const MonicaWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [activeICP, setActiveICP] = useState(null);
  const [projectId, setProjectId] = useState(null);
  
  // Lista de proyectos existentes
  const [existingProjects, setExistingProjects] = useState([]);
  const [showProjectsList, setShowProjectsList] = useState(false);
  
  // Resultados de cada paso
  const [mainIdea, setMainIdea] = useState('');
  const [videoDuration, setVideoDuration] = useState(30);
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [step3Data, setStep3Data] = useState(null);
  const [step4Data, setStep4Data] = useState(null);
  const [step5Data, setStep5Data] = useState(null);
  const [step6Data, setStep6Data] = useState(null);  // Scene selection
  const [scenePrompts, setScenePrompts] = useState({});  // {sceneId: promptData}
  const [currentSceneId, setCurrentSceneId] = useState(null);  // Escena actual siendo desarrollada
  const [stepFinalData, setStepFinalData] = useState(null);  // Music & Voice
  
  const [selectedConceptId, setSelectedConceptId] = useState(1);
  
  // Estados para el modal de regeneración con feedback
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');

  // Función para reproducir sonido de notificación
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Crear un oscilador para un sonido suave
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurar el tono (frecuencia agradable)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.2);
      
      oscillator.type = 'sine';
      
      // Volumen bajo para que sea sutil
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      // Reproducir
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio notification not available');
    }
  }, []);

  useEffect(() => {
    checkICPAndBusiness();
    loadExistingProjects();
  }, []);

  const checkICPAndBusiness = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      
      if (!response.data.business) {
        toast.error('Necesitas crear una empresa primero');
        navigate('/onboarding');
        return;
      }
      
      if (!response.data.icp) {
        toast.error('Necesitas crear un ICP primero');
        navigate('/icp-generator');
        return;
      }
      
      setActiveBusiness(response.data.business);
      setActiveICP(response.data.icp);
    } catch (error) {
      console.error('Error checking ICP:', error);
      toast.error('Error al cargar información');
    }
  };

  const loadExistingProjects = async () => {
    try {
      const response = await apiClient.get('/monica/projects');
      setExistingProjects(response.data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadProject = async (project) => {
    setProjectId(project.id);
    setMainIdea(project.main_idea);
    setVideoDuration(project.video_duration);
    setStep1Data(project.step_1_concepts);
    setStep2Data(project.step_2_script);
    setStep3Data(project.step_3_scenes);
    setStep4Data(project.step_4_assets);
    setStep5Data(project.step_5_prompts_assets);
    setStep6Data(project.step_6_scene_selection);
    setScenePrompts(project.scene_prompts || {});
    setStepFinalData(project.step_final_music_voice);
    
    // Determinar paso actual
    let nextStep = project.current_step;
    
    // Si current_step es 100, el proyecto está completado
    if (project.current_step === 100) {
      nextStep = 7;  // Mostrar paso final
    } else if (project.current_step === 6 && project.step_6_scene_selection) {
      // Si está en Step 6, verificar cuántas escenas faltan
      const totalScenes = project.step_6_scene_selection.total_scenes || 0;
      const completedScenes = Object.keys(project.scene_prompts || {}).length;
      if (completedScenes < totalScenes) {
        nextStep = 6;  // Continuar con escenas
      } else {
        nextStep = 7;  // Ir al paso final
      }
    }
    
    setCurrentStep(nextStep);
    setShowProjectsList(false);
    toast.success(`Proyecto "${project.main_idea.substring(0, 30)}..." cargado`);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!mainIdea.trim()) {
      toast.error('Describe la idea de tu video');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/monica/create-project', {
        main_idea: mainIdea,
        video_duration: videoDuration
      });
      
      setProjectId(response.data.project_id);
      toast.success('Proyecto creado! Generando conceptos...');
      
      // Automáticamente ejecutar Step 1
      await processStep(1, {}, response.data.project_id);
      
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Error al crear proyecto');
    } finally {
      setLoading(false);
    }
  };

  const processStep = async (stepNumber, stepData, projId = projectId) => {
    const startTime = Date.now();
    try {
      setLoading(true);
      
      const response = await apiClient.post(
        `/monica/project/${projId}/step/${stepNumber}`,
        stepData
      );
      
      // Guardar resultado según el paso
      if (stepNumber === 1) {
        setStep1Data(response.data);
        setCurrentStep(1);
      } else if (stepNumber === 2) {
        setStep2Data(response.data);
        setCurrentStep(2);
      } else if (stepNumber === 3) {
        setStep3Data(response.data);
        setCurrentStep(3);
      } else if (stepNumber === 4) {
        setStep4Data(response.data);
        setCurrentStep(4);
      } else if (stepNumber === 5) {
        setStep5Data(response.data);
        setCurrentStep(5);
      } else if (stepNumber === 6) {
        setStep6Data(response.data);
        setCurrentSceneId(null); // Asegurarse de volver a la lista de escenas
        setCurrentStep(6);
      }
      
      // Calcular tiempo de generación
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      
      // Reproducir sonido de notificación al completar
      playNotificationSound();
      toast.success(`Paso ${stepNumber} completado! ⏱️ ${duration}s`);
      console.log(`[DEV] Paso ${stepNumber} generado en ${duration} segundos`);
      
    } catch (error) {
      console.error(`Error in step ${stepNumber}:`, error);
      toast.error(`Error en paso ${stepNumber}: ${error.response?.data?.detail || 'Error desconocido'}`);
    } finally {
      setLoading(false);
    }
  };

  const regenerateStep = async (stepNumber) => {
    const stepData = stepNumber === 2 ? { selected_concept_id: selectedConceptId } : {};
    await processStep(stepNumber, stepData);
  };

  const handleSaveAndExit = () => {
    toast.success('Progreso guardado automáticamente');
    navigate('/dashboard');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  // Función para generar prompt de una escena individual
  const generateScenePrompt = async (sceneId) => {
    const startTime = Date.now();
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/monica/project/${projectId}/scene/${sceneId}/prompts`
      );
      
      // Actualizar los prompts de escenas
      setScenePrompts(prev => ({
        ...prev,
        [sceneId]: response.data
      }));
      
      // Actualizar step6Data con las escenas completadas
      if (step6Data) {
        const newCompletedScenes = [...(step6Data.scenes_completed || []), sceneId];
        const nextScene = step6Data.scenes_summary?.find(
          s => !newCompletedScenes.includes(s.id)
        );
        
        setStep6Data({
          ...step6Data,
          scenes_completed: newCompletedScenes,
          next_scene_id: nextScene?.id || null
        });
      }
      
      // Mostrar el detalle de la escena recién generada
      setCurrentSceneId(sceneId);
      
      // Calcular tiempo de generación
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      
      // Reproducir sonido de notificación
      playNotificationSound();
      toast.success(`Escena ${sceneId} generada! ⏱️ ${duration}s`);
      console.log(`[DEV] Escena ${sceneId} generada en ${duration} segundos`);
      
    } catch (error) {
      console.error('Error generating scene prompt:', error);
      toast.error(error.response?.data?.detail || 'Error al generar prompts de escena');
    } finally {
      setLoading(false);
    }
  };

  // Función para regenerar prompt de una escena con feedback opcional
  const regenerateScenePrompt = async (sceneId, feedback = '') => {
    const startTime = Date.now();
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/monica/project/${projectId}/scene/${sceneId}/prompts`,
        { feedback: feedback || undefined }
      );
      
      // Actualizar los prompts de escenas
      setScenePrompts(prev => ({
        ...prev,
        [sceneId]: response.data
      }));
      
      // Calcular tiempo de generación
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      
      // Reproducir sonido de notificación
      playNotificationSound();
      toast.success(`Escena ${sceneId} regenerada! ⏱️ ${duration}s`);
      console.log(`[DEV] Escena ${sceneId} regenerada en ${duration} segundos`);
      
    } catch (error) {
      console.error('Error regenerating scene prompt:', error);
      toast.error(error.response?.data?.detail || 'Error al regenerar prompts de escena');
    } finally {
      setLoading(false);
    }
  };

  // Función para volver a la lista de escenas
  const backToSceneList = () => {
    setCurrentSceneId(null);
  };

  // Función para ir al paso final (música y voz)
  const goToFinalStep = async () => {
    const startTime = Date.now();
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/monica/project/${projectId}/final-prompts`
      );
      
      setStepFinalData(response.data);
      setCurrentStep(7);
      
      // Calcular tiempo de generación
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      
      // Reproducir sonido de notificación
      playNotificationSound();
      toast.success(`Audio Final generado! ⏱️ ${duration}s 🎉`);
      console.log(`[DEV] Audio Final generado en ${duration} segundos`);
      
    } catch (error) {
      console.error('Error generating final prompts:', error);
      toast.error(error.response?.data?.detail || 'Error al generar prompts finales');
    } finally {
      setLoading(false);
    }
  };

  // Función para regenerar prompts finales
  const regenerateFinalStep = async () => {
    await goToFinalStep();
  };

  const renderStepIndicator = () => {
    const steps = [
      { num: 0, label: 'Idea' },
      { num: 1, label: 'Conceptos' },
      { num: 2, label: 'Guion' },
      { num: 3, label: 'Escenas' },
      { num: 4, label: 'Activos' },
      { num: 5, label: 'Nano Banana' },
      { num: 6, label: 'Prompts x Escena' },
      { num: 7, label: 'Audio Final' }
    ];

    return (
      <div className="flex items-center justify-center gap-2 mb-8">
        {steps.map((step, idx) => (
          <React.Fragment key={step.num}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs ${
                currentStep >= step.num ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > step.num ? <CheckCircle2 className="w-5 h-5" /> : step.num}
              </div>
              <span className="text-xs mt-1 text-gray-600 text-center">{step.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-1 w-8 ${currentStep > step.num ? 'bg-purple-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderProjectsList = () => (
    <Card className="max-w-3xl mx-auto mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Proyectos Existentes
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowProjectsList(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {existingProjects.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No tienes proyectos guardados</p>
        ) : (
          <div className="space-y-3">
            {existingProjects.map((project) => (
              <Card 
                key={project.id} 
                className="cursor-pointer hover:border-purple-400 transition-colors"
                onClick={() => loadProject(project)}
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">
                        {project.main_idea.substring(0, 60)}...
                      </h4>
                      <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
                        <span>⏱️ {project.video_duration}s</span>
                        <span>📊 Paso {project.current_step}/6</span>
                        <span className={`px-2 py-1 rounded ${
                          project.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {project.status === 'completed' ? 'Completado' : 'En progreso'}
                        </span>
                      </div>
                      {project.created_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          📅 Creado: {new Date(project.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="outline">
                      Cargar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep0 = () => (
    <>
      {showProjectsList && renderProjectsList()}
      
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Video className="w-6 h-6 text-purple-600" />
            Bienvenido a MÓNICA
          </CardTitle>
          <CardDescription className="text-base">
            Directora Creativa AI para Videos Cinematográficos
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeBusiness && activeICP && (
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm font-semibold text-purple-900 mb-2">
                📌 Creando video para: {activeBusiness.business_description}
              </p>
              <p className="text-xs text-purple-700">
                Target: {activeICP.demographics?.age_range} • 
                {activeICP.pain_points?.[0]}
              </p>
            </div>
          )}

          {existingProjects.length > 0 && !showProjectsList && (
            <Button 
              variant="outline" 
              className="w-full mb-4"
              onClick={() => setShowProjectsList(true)}
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Cargar Proyecto Existente ({existingProjects.length})
            </Button>
          )}

          <form onSubmit={handleCreateProject} className="space-y-6">
            <div>
              <Label htmlFor="mainIdea" className="text-lg">
                ¿Qué video quieres crear? *
              </Label>
              <Textarea
                id="mainIdea"
                value={mainIdea}
                onChange={(e) => setMainIdea(e.target.value)}
                placeholder="Ej: Un video estilo Apple mostrando nuestro producto, enfocado en cómo simplifica la vida del usuario..."
                rows={4}
                required
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-2">
                Describe tu idea principal. MÓNICA se encargará del resto.
              </p>
            </div>

            <div>
              <Label htmlFor="duration" className="text-lg">
                Duración del Video *
              </Label>
              <div className="grid grid-cols-4 gap-3 mt-2">
                {[15, 30, 60, 90].map((seconds) => (
                  <Button
                    key={seconds}
                    type="button"
                    variant={videoDuration === seconds ? 'default' : 'outline'}
                    onClick={() => setVideoDuration(seconds)}
                    className="h-16"
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold">{seconds}s</div>
                      <div className="text-xs opacity-70">
                        {seconds === 15 && 'Rápido'}
                        {seconds === 30 && 'Ideal'}
                        {seconds === 60 && 'Completo'}
                        {seconds === 90 && 'Extenso'}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">🎬 Qué hace MÓNICA:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✅ Genera 3 conceptos cinematográficos</li>
                <li>✅ Escribe el guion completo</li>
                <li>✅ Divide en escenas ultra-detalladas</li>
                <li>✅ Identifica activos visuales clave</li>
                <li>✅ Crea prompts Nano Banana para imágenes</li>
                <li>✅ Genera prompts para Runway (video) + música</li>
                <li>✅ Todo personalizado según tu ICP</li>
              </ul>
            </div>

            <Button type="submit" className="w-full h-14 text-lg" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Creando proyecto y generando conceptos...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 w-5 h-5" />
                  Comenzar con MÓNICA
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );

  const renderStep1 = () => (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 1: Selecciona un Concepto</CardTitle>
            <CardDescription>
              MÓNICA generó 3 conceptos cinematográficos basados en tu ICP
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
            <Button variant="outline" size="sm" onClick={() => regenerateStep(1)} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {step1Data && (
          <div className="space-y-6">
            <div className="grid gap-6">
              {step1Data.concepts?.map((concept) => (
                <Card 
                  key={concept.id}
                  className={`cursor-pointer transition-all ${
                    selectedConceptId === concept.id 
                      ? 'border-2 border-purple-600 bg-purple-50' 
                      : 'border hover:border-purple-300'
                  }`}
                  onClick={() => setSelectedConceptId(concept.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl">{concept.title}</CardTitle>
                        <p className="text-sm text-gray-600 mt-2">{concept.description}</p>
                      </div>
                      {concept.id === step1Data.recommended_concept && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                          Recomendado
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        💡 Por qué funciona con tu ICP:
                      </p>
                      <p className="text-sm text-gray-600">{concept.why}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Estilo: {concept.style}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button 
              onClick={() => processStep(2, { selected_concept_id: selectedConceptId })}
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Generando guion...
                </>
              ) : (
                <>
                  Continuar con Concepto {selectedConceptId}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 2: Guion Completo</CardTitle>
            <CardDescription>
              MÓNICA escribió un guion cinematográfico basado en tu ICP
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
            <Button variant="outline" size="sm" onClick={() => regenerateStep(2)} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {step2Data && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">📝 Guion (Narración)</h3>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleCopy(step2Data.narration)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </Button>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                {step2Data.narration}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">🎭 Arco Emocional</h4>
                <div className="space-y-1">
                  {step2Data.emotional_arc?.map((emotion, idx) => (
                    <div key={idx} className="text-sm text-blue-800 capitalize">
                      {idx + 1}. {emotion}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">💬 Mensajes Clave</h4>
                {step2Data.key_messages?.map((msg, idx) => (
                  <div key={idx} className="text-sm text-purple-800 mb-1">
                    • {msg}
                  </div>
                ))}
              </div>
            </div>

            <Button 
              onClick={() => processStep(3, {})}
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Dividiendo en escenas...
                </>
              ) : (
                <>
                  Siguiente: Dividir en Escenas Detalladas
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 3: Escenas Ultra-Detalladas</CardTitle>
            <CardDescription>
              Duración total: {step3Data?.total_duration} • Estilo: {step3Data?.overall_style_guide}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
            <Button variant="outline" size="sm" onClick={() => regenerateStep(3)} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {step3Data && (
          <div className="space-y-6">
            <div className="grid gap-4">
              {step3Data.scenes?.map((scene) => (
                <Card key={scene.id} className="border-2">
                  <CardHeader className="bg-gray-50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        Escena {scene.id}
                      </CardTitle>
                      <span className="text-sm text-gray-600">{scene.duration}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    <div>
                      <span className="font-semibold text-sm">🎬 Acción:</span>
                      <p className="text-sm text-gray-700">{scene.action}</p>
                    </div>
                    
                    {scene.visual_details && (
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="font-semibold text-sm text-blue-900">🎨 Detalles Visuales:</span>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-blue-800">
                          <div><strong>Plano:</strong> {scene.visual_details.shot_type}</div>
                          <div><strong>Cámara:</strong> {scene.visual_details.camera_movement}</div>
                          <div><strong>Iluminación:</strong> {scene.visual_details.lighting}</div>
                          <div><strong>Composición:</strong> {scene.visual_details.composition}</div>
                        </div>
                        <p className="text-xs text-blue-700 mt-2">
                          <strong>Colores:</strong> {scene.visual_details.color_palette}
                        </p>
                      </div>
                    )}

                    {scene.emotional_tone && (
                      <div className="bg-purple-50 p-3 rounded">
                        <span className="font-semibold text-sm text-purple-900">💭 Tono Emocional:</span>
                        <div className="text-xs text-purple-800 mt-2">
                          <div><strong>Emoción:</strong> {scene.emotional_tone.primary_emotion}</div>
                          <div><strong>Mood:</strong> {scene.emotional_tone.mood}</div>
                          <div><strong>Energía:</strong> {scene.emotional_tone.energy_level}</div>
                        </div>
                      </div>
                    )}

                    {scene.branding_elements && (
                      <div className="bg-green-50 p-3 rounded">
                        <span className="font-semibold text-sm text-green-900">🎯 Branding:</span>
                        <div className="text-xs text-green-800 mt-2">
                          <div><strong>Estilo:</strong> {scene.branding_elements.visual_style}</div>
                          <div><strong>Presencia:</strong> {scene.branding_elements.brand_presence}</div>
                        </div>
                      </div>
                    )}

                    <div>
                      <span className="font-semibold text-sm">📝 Narración:</span>
                      <p className="text-sm text-gray-700 italic">{scene.narration_part}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-sm">📍 Locación:</span>
                      <span className="text-sm text-gray-700 ml-2">{scene.location}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button 
              onClick={() => processStep(4, {})}
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Identificando activos creativos...
                </>
              ) : (
                <>
                  Siguiente: Identificar Activos Creativos
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep4 = () => (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 4: Activos Creativos</CardTitle>
            <CardDescription>
              Elementos visuales clave que deben existir antes de crear las escenas
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
            <Button variant="outline" size="sm" onClick={() => regenerateStep(4)} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {step4Data && (
          <div className="space-y-6">
            <div className="grid gap-4">
              {step4Data.assets?.map((asset, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{asset.name}</CardTitle>
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          asset.importance === 'critical' ? 'bg-red-100 text-red-800' :
                          asset.importance === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {asset.importance}
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold capitalize">
                          {asset.type}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 mb-2">{asset.description}</p>
                    <p className="text-xs text-gray-500">
                      Aparece en escenas: {asset.appears_in_scenes?.join(', ')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button 
              onClick={() => processStep(5, {})}
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Generando prompts Nano Banana...
                </>
              ) : (
                <>
                  Siguiente: Generar Prompts Nano Banana
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep5 = () => (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Paso 5: Prompts Nano Banana</CardTitle>
            <CardDescription>
              Prompts ultra-detallados para Gemini Imagen 3 (Nano Banana)
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSaveAndExit}>
              <Save className="w-4 h-4 mr-2" />
              Guardar y Salir
            </Button>
            <Button variant="outline" size="sm" onClick={() => regenerateStep(5)} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {step5Data && (
          <div className="space-y-6">
            {step5Data.asset_prompts?.map((promptData, idx) => (
              <Card key={idx} className="border-2 border-purple-200">
                <CardHeader className="bg-purple-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{promptData.asset_name}</CardTitle>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleCopy(promptData.prompt)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar Prompt
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="bg-white p-4 rounded border">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                      🎨 Prompt Principal:
                    </p>
                    <p className="text-sm font-mono text-gray-800">
                      {promptData.prompt}
                    </p>
                  </div>
                  
                  {promptData.negative_prompt && (
                    <div className="bg-red-50 p-3 rounded border border-red-200">
                      <p className="text-xs font-semibold text-red-900 mb-1">
                        ❌ Negative Prompt:
                      </p>
                      <p className="text-xs font-mono text-red-800">
                        {promptData.negative_prompt}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>🤖 Modelo: {promptData.model}</span>
                    <span>🎨 Referencia: {promptData.style_reference}</span>
                    {promptData.technical_params && (
                      <span>📐 Ratio: {promptData.technical_params.aspect_ratio}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button 
              onClick={() => processStep(6, {})}
              className="w-full h-12"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Generando prompts finales...
                </>
              ) : (
                <>
                  Siguiente: Prompts Finales (Escenas + Música)
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Renderizar Step 6 usando los nuevos componentes
  const renderStep6New = () => {
    // Si hay una escena seleccionada, mostrar el detalle
    if (currentSceneId !== null) {
      return renderScenePromptDetail({
        currentSceneId,
        scenePrompts,
        step3Data,
        step6Data,
        loading,
        handleCopy,
        handleSaveAndExit,
        regenerateScenePrompt,
        backToSceneList,
        goToFinalStep,
        showRegenerateModal,
        setShowRegenerateModal,
        regenerateFeedback,
        setRegenerateFeedback
      });
    }
    
    // Si no, mostrar la lista de escenas
    return renderStep6({
      step6Data,
      scenePrompts,
      currentSceneId,
      loading,
      handleSaveAndExit,
      regenerateStep: () => regenerateStep(6),
      generateScenePrompt,
      setCurrentSceneId,
      goToFinalStep,
      step3Data
    });
  };

  // Renderizar Step 7 (Final) usando el nuevo componente
  const renderStep7New = () => {
    return renderStepFinal({
      stepFinalData,
      loading,
      handleCopy,
      handleSaveAndExit,
      regenerateFinalStep,
      navigate
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              MÓNICA - Directora Creativa AI
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Videos cinematográficos con prompts ultra-detallados
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {currentStep > 0 && renderStepIndicator()}

        <div className="mb-8">
          {currentStep === 0 && renderStep0()}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
          {currentStep === 6 && renderStep6New()}
          {currentStep === 7 && renderStep7New()}
        </div>
      </div>
    </div>
  );
};

export default MonicaWizard;
